import type { ImpersonationTarget } from '../api/impersonationApi';

/**
 * Impersonation session handling on the client.
 *
 * Auth is stateless base64 credentials in localStorage; to "become" a user we swap the
 * active session keys for the target's (using a signed impersonation token) and keep a
 * backup of the original super-admin session so we can restore it later. A full page
 * reload is used on enter/exit because App.js reads userName/userAccountId from
 * localStorage at init and does not refresh them from /me.
 */

// Active session keys (read by App.js / fetchWithAuth)
const ACTIVE = {
  userName: 'userName',
  userAccountId: 'userAccountId',
  userId: 'userId',
  userAuthToken: 'userAuthToken',
  selectedServiceDesk: 'selected-service-desk',
} as const;

// Backup of the original (impersonator) session
const ORIG = {
  userName: 'imp_orig_userName',
  userAccountId: 'imp_orig_userAccountId',
  userId: 'imp_orig_userId',
  userAuthToken: 'imp_orig_userAuthToken',
  selectedServiceDesk: 'imp_orig_selected-service-desk',
} as const;

/** True when an impersonation session is currently active (a backup exists). */
export const isImpersonating = (): boolean => localStorage.getItem(ORIG.userAuthToken) !== null;

/**
 * Remove the impersonator backup keys. Logout / session-invalidation paths must call this:
 * a session that ends mid-impersonation (logout, expired token) otherwise leaves the backup
 * behind, keeping isImpersonating() true forever and silently blocking future beginImpersonation.
 */
export const clearImpersonationBackup = (): void => {
  Object.values(ORIG).forEach((key) => localStorage.removeItem(key));
};

/**
 * Enter impersonation: back up the current session, swap in the target identity + token,
 * then hard-reload so the app re-initialises as the target user.
 */
export const beginImpersonation = ({
  token,
  target,
}: {
  token: string;
  target: ImpersonationTarget;
}): void => {
  // Guard against nesting: never overwrite an existing backup.
  if (isImpersonating()) return;

  localStorage.setItem(ORIG.userName, localStorage.getItem(ACTIVE.userName) ?? '');
  localStorage.setItem(ORIG.userAccountId, localStorage.getItem(ACTIVE.userAccountId) ?? '');
  localStorage.setItem(ORIG.userId, localStorage.getItem(ACTIVE.userId) ?? '');
  localStorage.setItem(ORIG.userAuthToken, localStorage.getItem(ACTIVE.userAuthToken) ?? '');
  localStorage.setItem(ORIG.selectedServiceDesk, localStorage.getItem(ACTIVE.selectedServiceDesk) ?? '');

  localStorage.setItem(ACTIVE.userName, target.username);
  localStorage.setItem(ACTIVE.userAccountId, target.accountId ?? '');
  localStorage.setItem(ACTIVE.userId, target.userId);
  localStorage.setItem(ACTIVE.userAuthToken, token);
  localStorage.removeItem(ACTIVE.selectedServiceDesk);

  window.location.href = '/home';
};

/**
 * Exit impersonation: restore the original super-admin session from the backup,
 * clear the backup, then hard-reload back into the admin dashboard.
 */
export const endImpersonation = (): void => {
  if (!isImpersonating()) return;

  localStorage.setItem(ACTIVE.userName, localStorage.getItem(ORIG.userName) ?? '');
  localStorage.setItem(ACTIVE.userAccountId, localStorage.getItem(ORIG.userAccountId) ?? '');
  localStorage.setItem(ACTIVE.userId, localStorage.getItem(ORIG.userId) ?? '');
  localStorage.setItem(ACTIVE.userAuthToken, localStorage.getItem(ORIG.userAuthToken) ?? '');

  const originalDesk = localStorage.getItem(ORIG.selectedServiceDesk);
  if (originalDesk) {
    localStorage.setItem(ACTIVE.selectedServiceDesk, originalDesk);
  } else {
    localStorage.removeItem(ACTIVE.selectedServiceDesk);
  }

  clearImpersonationBackup();

  window.location.href = '/admin';
};
